import { Link, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Menu, ShieldAlert, LogOut, Sparkles, HelpCircle } from "lucide-react";
import { useAuth } from "@/lib/auth";
import Sidebar from "@/components/Sidebar";
import DemoTour from "@/components/DemoTour";
import ProductTour from "@/components/ProductTour";
import NotificationBell from "@/components/NotificationBell";
import { MobileNavProvider, useMobileNav } from "@/lib/mobileNav";
import { toast } from "sonner";

const DEMO_TENANT_PREFIX = "demo-";

function TopBar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = () => {
    logout();
    toast.success("Signed out");
    navigate("/login", { replace: true });
  };
  return (
    <div
      className="w-full px-4 sm:px-8 lg:px-12 py-2.5 border-b flex items-center justify-end gap-3 bg-[var(--ll-bg-card)]"
      style={{ borderColor: "var(--ll-border)" }}
      data-testid="app-topbar"
    >
      {user && (
        <div className="text-right leading-tight hidden sm:block">
          <div className="text-xs font-medium truncate max-w-[16rem]" data-testid="topbar-user-email">{user.email}</div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">{user.role}</div>
        </div>
      )}
      <NotificationBell />
      <button
        onClick={handleLogout}
        data-testid="topbar-logout-btn"
        className="flex items-center gap-1.5 text-xs px-3 py-1.5 border hover:bg-[var(--ll-mint)] transition-colors"
        style={{ borderColor: "var(--ll-border)" }}
        title="Sign out"
      >
        <LogOut size={13} /> Sign out
      </button>
    </div>
  );
}

export default function AppLayout() {
  const { user, impersonating, exitImpersonation, tenant, logout } = useAuth();
  const location = useLocation();
  if (!user && !localStorage.getItem("ll_token")) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  // Clients live in a separate portal at /client — they should never see the
  // firm's sidebar, reports, or team.
  if (user?.role === "client") {
    return <Navigate to="/client" replace />;
  }
  const isDemo = tenant?.id?.startsWith(DEMO_TENANT_PREFIX);
  return (
    <MobileNavProvider>
      <div className="min-h-screen flex flex-col" style={{ background: "var(--ll-bg)" }}>
        {isDemo && !impersonating && (
          <div
            className="w-full px-4 py-2 flex flex-wrap items-center justify-center gap-3 text-xs sticky top-0 z-50"
            style={{ background: "var(--ll-ochre)", color: "white" }}
            data-testid="demo-banner"
          >
            <Sparkles size={14} />
            <span className="font-medium">
              You&apos;re in a <strong>demo workspace</strong>. Changes will be wiped within 24 hours.
            </span>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("ll:demo-tour:show"))}
              data-testid="demo-banner-tour-btn"
              className="px-2.5 py-1 bg-white/10 hover:bg-white/25 text-white font-medium transition-colors inline-flex items-center gap-1"
            >
              <HelpCircle size={12} /> Show tour
            </button>
            <Link
              to="/signup"
              onClick={() => { logout(); }}
              data-testid="demo-banner-signup-btn"
              className="px-3 py-1 bg-white/15 hover:bg-white/25 text-white font-medium transition-colors"
            >
              Sign up to save your work →
            </Link>
          </div>
        )}
        <DemoTour active={isDemo && !impersonating} />
        {!isDemo && <ProductTour />}
        {impersonating && (
          <div
            className="w-full px-4 py-2 flex items-center justify-center gap-3 text-xs sticky top-0 z-50"
            style={{ background: "var(--ll-ochre)", color: "white" }}
            data-testid="impersonation-banner"
          >
            <ShieldAlert size={14} />
            <span className="font-medium">
              Support session · You are viewing <strong>{tenant?.name || impersonating.target_tenant}</strong> as <strong>{user?.email || impersonating.target_email}</strong>
            </span>
            <button
              type="button"
              onClick={() => { exitImpersonation(); toast.success("Returned to platform admin"); }}
              data-testid="exit-impersonation-btn"
              className="ml-2 px-3 py-1 bg-white/15 hover:bg-white/25 text-white flex items-center gap-1.5 transition-colors"
            >
              <LogOut size={11} /> Exit support session
            </button>
          </div>
        )}
        <div className="flex flex-1 min-h-0">
          <Sidebar />
          <main className="flex-1 min-w-0 w-full flex flex-col">
            <TopBar />
            <div className="flex-1 min-w-0">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </MobileNavProvider>
  );
}

export function PageHeader({ title, subtitle, actions, testid }) {
  const { openNav } = useMobileNav();
  return (
    <div
      className="px-4 sm:px-8 lg:px-12 pt-6 sm:pt-10 pb-5 sm:pb-6 border-b"
      style={{ borderColor: "var(--ll-border)" }}
      data-testid={testid}
    >
      <div className="flex flex-wrap items-end justify-between gap-3 sm:gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <button
            type="button"
            onClick={openNav}
            data-testid="mobile-nav-toggle"
            aria-label="Open navigation"
            className="lg:hidden mt-1 w-9 h-9 border grid place-items-center hover:bg-[var(--ll-mint)] transition-colors"
            style={{ borderColor: "var(--ll-border)" }}
          >
            <Menu size={16} />
          </button>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] mb-2">{subtitle || "Workspace"}</div>
            <h1 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none truncate">{title}</h1>
          </div>
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}

export function PageBody({ children, className = "" }) {
  return <div className={`px-4 sm:px-8 lg:px-12 py-6 sm:py-8 ${className}`}>{children}</div>;
}
